import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, CheckCircle, Trash2, RefreshCw, Search, AlertTriangle, ServerCrash, XCircle, Wifi, WifiOff, CheckCircle2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { wpApprove, wpDelete, wpGet, wpList, wpTestConnection, formatWpError } from "@/lib/wpSubmissions";

type AnyObj = Record<string, any>;

interface ConnectionError {
  title: string;
  description: string;
  isWpError: boolean;
  isRetryable: boolean;
}

interface ConnectionStatus {
  tested: boolean;
  success: boolean;
  message: string;
}

function getWpId(item: AnyObj): string {
  return String(item?.id ?? item?.wp_id ?? item?.ID ?? "");
}

function getAddressSummary(item: AnyObj): string {
  const addr = item?.address_1 ?? item?.address ?? item?.street ?? "";
  const city = item?.city ?? "";
  const state = item?.state ?? item?.state_abbr ?? item?.state_code ?? "";
  const zip = item?.zip ?? item?.postal_code ?? "";
  return [addr, city, state, zip].filter(Boolean).join(", ");
}

export default function AdminWpPropertySubmissions() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<AnyObj[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const perPage = 50;

  const [connectionError, setConnectionError] = useState<ConnectionError | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [testing, setTesting] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState<AnyObj | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const sendNotification = async (item: AnyObj) => {
    try {
      const { error } = await supabase.functions.invoke("send-wp-submission-notification", {
        body: {
          submissionType: "property",
          submissionId: getWpId(item),
          submissionDetails: {
            name: item?.name || item?.property_name || "Property Submission",
            address: getAddressSummary(item),
            submitterEmail: item?.contact_email || item?.email,
            submitterName: item?.contact_name || item?.submitted_by || item?.name,
          },
        },
      });
      if (error) throw error;
      toast.success("Email notification sent to admins");
    } catch (e: any) {
      console.error("Failed to send notification:", e);
      toast.error("Failed to send notification: " + (e.message || "Unknown error"));
    }
  };

  const handleNotifyAll = async () => {
    if (items.length === 0) {
      toast.info("No submissions to notify about");
      return;
    }
    setNotifying(true);
    try {
      const { error } = await supabase.functions.invoke("send-wp-submission-notification", {
        body: {
          submissionType: "property",
          submissionId: "batch",
          submissionDetails: {
            name: `${items.length} pending property submission(s)`,
            address: "Multiple properties awaiting review",
          },
        },
      });
      if (error) throw error;
      toast.success("Notification sent to all admins");
    } catch (e: any) {
      console.error("Failed to send notification:", e);
      toast.error("Failed to send notification: " + (e.message || "Unknown error"));
    } finally {
      setNotifying(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    setConnectionError(null);
    
    try {
      const result = await wpTestConnection();
      setConnectionStatus({
        tested: true,
        success: result.success,
        message: result.message,
      });
      
      if (result.success) {
        toast.success("WordPress connection successful");
      } else {
        toast.error(result.message);
      }
    } catch (e: any) {
      const formatted = formatWpError(e);
      setConnectionStatus({
        tested: true,
        success: false,
        message: formatted.description,
      });
      toast.error(formatted.description);
    } finally {
      setTesting(false);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    setConnectionError(null);
    try {
      const res = await wpList<AnyObj>("properties", { page, perPage });
      setItems(res.items || []);
      setTotal(res.total || 0);
      // Clear any previous connection status on successful fetch
      if (connectionStatus?.success === false) {
        setConnectionStatus(null);
      }
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      setConnectionError(formatted);
      toast.error(formatted.description);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((it) => {
      const blob = JSON.stringify(it).toLowerCase();
      return blob.includes(q);
    });
  }, [items, search]);

  const handleView = async (it: AnyObj) => {
    setViewOpen(true);
    setViewItem(it);

    const id = getWpId(it);
    if (!id) return;

    setViewLoading(true);
    try {
      const res = await wpGet<AnyObj>("properties", id);
      setViewItem(res.item);
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    } finally {
      setViewLoading(false);
    }
  };

  const handleApprove = async (it: AnyObj) => {
    const id = getWpId(it);
    if (!id) return toast.error("Missing WordPress id");

    try {
      await wpApprove("properties", id);
      toast.success("Approved in WordPress");
      fetchList();
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    }
  };

  const handleDelete = async (it: AnyObj) => {
    const id = getWpId(it);
    if (!id) return toast.error("Missing WordPress id");

    if (!confirm("Delete this submission from WordPress?")) return;

    try {
      await wpDelete("properties", id);
      toast.success("Deleted from WordPress");
      fetchList();
    } catch (e: any) {
      console.error(e);
      const formatted = formatWpError(e);
      toast.error(formatted.description);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <CardTitle>WordPress Property Submissions</CardTitle>
              <CardDescription>
                Pending property submissions coming from tpshousing.com (WordPress forms).
              </CardDescription>
            </div>
            <Button
              variant={connectionStatus?.success ? "outline" : "secondary"}
              size="sm"
              onClick={handleTestConnection}
              disabled={testing}
              className="shrink-0"
            >
              {testing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Testing…
                </>
              ) : connectionStatus?.success ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                  Connected
                </>
              ) : connectionStatus?.tested ? (
                <>
                  <WifiOff className="h-4 w-4 mr-2 text-destructive" />
                  Test Failed
                </>
              ) : (
                <>
                  <Wifi className="h-4 w-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {connectionStatus && !connectionStatus.success && (
            <Alert variant="destructive" className="mb-4">
              <WifiOff className="h-4 w-4" />
              <AlertTitle className="flex items-center gap-2">
                Connection Test Failed
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-auto"
                  onClick={() => setConnectionStatus(null)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </AlertTitle>
              <AlertDescription className="mt-2">
                <p>{connectionStatus.message}</p>
              </AlertDescription>
            </Alert>
          )}

          {connectionStatus?.success && (
            <Alert className="mb-4 border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Connection Successful</AlertTitle>
              <AlertDescription>{connectionStatus.message}</AlertDescription>
            </Alert>
          )}

          {connectionError && (
            <Alert variant="destructive" className="mb-4">
              {connectionError.isWpError ? (
                <ServerCrash className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <AlertTitle className="flex items-center gap-2">
                {connectionError.title}
                {connectionError.isRetryable && (
                  <Badge variant="outline" className="text-xs">Retried automatically</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-auto"
                  onClick={() => setConnectionError(null)}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </AlertTitle>
              <AlertDescription className="mt-2">
                <p>{connectionError.description}</p>
                {connectionError.isWpError && (
                  <p className="text-xs mt-2 opacity-80">
                    This is a WordPress server issue. Contact your WordPress administrator or check the server logs.
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between mb-4">
            <div className="flex items-center gap-2 w-full md:max-w-md">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search submissions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleNotifyAll} disabled={notifying || items.length === 0}>
                <Mail className={`h-4 w-4 mr-2 ${notifying ? "animate-pulse" : ""}`} />
                {notifying ? "Sending…" : "Notify Admins"}
              </Button>
              <Button variant="outline" onClick={fetchList} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary">Total: {total}</Badge>
            <Badge variant="outline">Page {page + 1} / {pageCount}</Badge>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ width: 80 }}>ID</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead style={{ width: 100 }}>Type</TableHead>
                  <TableHead style={{ width: 150 }}>Submitted By</TableHead>
                  <TableHead style={{ width: 150 }}>Submitted At</TableHead>
                  <TableHead style={{ width: 220 }}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => (
                  <TableRow key={getWpId(it) || JSON.stringify(it)}>
                    <TableCell className="font-mono text-xs">{getWpId(it) || "—"}</TableCell>
                    <TableCell>{getAddressSummary(it) || it?.address_1 || it?.address || "—"}</TableCell>
                    <TableCell>{it?.type || it?.property_type || "—"}</TableCell>
                    <TableCell>{it?.submitted_by || it?.contact_name || it?.name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {it?.submitted_at ? new Date(it.submitted_at).toLocaleDateString() : it?.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => handleView(it)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => sendNotification(it)}>
                          <Mail className="h-4 w-4 mr-2" />
                          Notify
                        </Button>
                        <Button size="sm" onClick={() => handleApprove(it)}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(it)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && filtered.length === 0 && !connectionError && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No pending submissions.
                    </TableCell>
                  </TableRow>
                )}

                {!loading && connectionError && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Could not load submissions. See error above.
                    </TableCell>
                  </TableRow>
                )}

                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <Button variant="outline" disabled={page <= 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Showing {filtered.length} of {items.length} on this page
            </div>
            <Button
              variant="outline"
              disabled={page >= pageCount - 1 || loading}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
            <DialogDescription>
              {viewLoading ? "Loading latest details from WordPress…" : ""}
            </DialogDescription>
          </DialogHeader>
          <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-[60vh]">
            {JSON.stringify(viewItem, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
